/**
 * useAuth Hook - INFRASTRUCTURE COMPONENT
 * ═══════════════════════════════════════════════════════════════════════════
 * Single source of truth for authentication and organization context
 *
 * REPLACES:
 * - SecureStorage.getPfuserUuid() scattered calls
 * - Duplicate API calls to load org associations
 * - Local state management for user/org data
 *
 * PROVIDES:
 * - JWT validation (isAuthenticated)
 * - User context (pfUser.UserUUID, Username, etc.)
 * - Organization associations (userOrganizations array)
 * - Active org context (activeOrganizationId, activeOrganizationUUID)
 * - Role flags (isSuperAdmin, isOrgAdmin)
 * - Executive access flags
 *
 * DATA FLOW:
 * 1. PlatformHeader loads data ONCE at app mount
 * 2. Stores in Redux global state
 * 3. All components consume via useAuth() hook
 * 4. NO duplicate API calls, NO relative path conflicts
 *
 * USAGE:
 * ```tsx
 * import { useAuth } from '../../utils/useAuth';
 *
 * const MyComponent = () => {
 *     const {
 *         isAuthenticated,
 *         pfUser,
 *         userOrganizations,
 *         activeOrganizationUUID,
 *         isSuperAdmin,
 *         isOrgAdmin
 *     } = useAuth();
 *
 *     // Filter API calls by active org
 *     const data = await apiRequest('/api/endpoint', 'POST', {
 *         Manage: 5,
 *         OrganizationUuid: activeOrganizationUUID,
 *     });
 *
 *     return <div>...</div>;
 * };
 * ```
 *
 * Created: 2026-06-26 (Infrastructure Component Pattern)
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../app/store';
import type { Organization, PFUser, RoleAssignment } from '../app/redux/auth/authSlice';

export interface UseAuthResult {
    // Authentication state
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    accessToken: string | null;

    // User context
    pfUser: PFUser | null;

    // Organization context
    userOrganizations: Organization[];
    activeOrganizationUUID: string | null;
    activeOrgRoleId: number | null;

    // Role assignments
    userRoleAssignments: RoleAssignment[];

    // Role flags
    isSuperAdmin: boolean;
    isOrgAdmin: boolean;
    hasExecutiveAccess: boolean;
    executiveAccessType?: number;
    executiveAccessName?: string;

    // Permissions
    permissions: string[];
}

/**
 * Infrastructure hook for accessing authentication and organization context
 * All components should use this instead of SecureStorage or duplicate API calls
 */
export const useAuth = (): UseAuthResult => {
    const auth = useSelector((state: RootState) => state.auth);

    // FIX (2026-10-23): This hook is called by 100+ components across the
    // entire routed tree (every mod_/fixmod_/scr_ host, ComposerHost,
    // ModuleHost, BrandingProvider, etc.). It previously did two things on
    // EVERY render of EVERY caller:
    //   1. An unconditional console.log (fired in production too - with this
    //      many call sites mounted/re-rendering across the app, this alone
    //      produces a large, continuous volume of main-thread console I/O
    //      and object serialization).
    //   2. Returned a brand-new object literal every call, so any consumer
    //      that passed the whole hook result (or a destructured piece of it)
    //      into a dependency array, prop, or React.memo comparison saw a
    //      "changed" value every single render even when nothing in Redux
    //      actually changed - defeating memoization everywhere this hook is
    //      used.
    // Together these were prime suspects for exactly the kind of render-storm
    // that previously starved Suspense/lazy commits elsewhere in this app
    // (see AppContent's React.memo comment and
    // /memories/repo/apiservice-stale-route-context-bug-fix-2026-09-08.md
    // sibling fixes). Removed the log; memoized the return value so it's
    // referentially stable unless the underlying auth fields actually change.
    const activeOrg = useMemo(
        () =>
            auth.userOrganizations.find(
                (org) => org.organization_uuid === auth.activeOrganizationUUID,
            ),
        [auth.userOrganizations, auth.activeOrganizationUUID],
    );

    return useMemo<UseAuthResult>(
        () => ({
            isAuthenticated: auth.isAuthenticated,
            isLoading: auth.isLoading,
            error: auth.error,
            accessToken: auth.accessToken,
            pfUser: auth.pfUser,
            userOrganizations: auth.userOrganizations,
            activeOrganizationUUID: auth.activeOrganizationUUID,
            activeOrgRoleId: auth.activeOrgRoleId,
            userRoleAssignments: auth.userRoleAssignments,
            isSuperAdmin: auth.isSuperAdmin, // ⭐ UPDATED 2026-07-03: Use dedicated TGAUTH.Auth_TGSuperAdmins (NOT org association)
            isOrgAdmin:
                activeOrg?.is_organization_admin || activeOrg?.is_organization_superadmin || false,
            hasExecutiveAccess: auth.hasExecutiveAccess,
            executiveAccessType: auth.executiveAccessType,
            executiveAccessName: auth.executiveAccessName,
            permissions: auth.permissions,
        }),
        [
            auth.isAuthenticated,
            auth.isLoading,
            auth.error,
            auth.accessToken,
            auth.pfUser,
            auth.userOrganizations,
            auth.activeOrganizationUUID,
            auth.activeOrgRoleId,
            auth.userRoleAssignments,
            auth.isSuperAdmin,
            activeOrg,
            auth.hasExecutiveAccess,
            auth.executiveAccessType,
            auth.executiveAccessName,
            auth.permissions,
        ],
    );
};
